import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Store, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { BentaLogo } from '../components/BentaLogo';

// Verified against POST /api/auth/login and /api/auth/platform/login on 2026-09-06.
const TENANT_DEMOS = [
  { role: 'Owner', email: 'owner@demo.com', password: 'password123' },
  { role: 'Store Manager', email: 'manager@demo.com', password: 'password123' },
  { role: 'Cashier', email: 'cashier@demo.com', password: 'password123' },
];
const PLATFORM_DEMOS = [
  { role: 'Platform Admin', email: 'admin@phpos.com', password: 'admin123' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tenantForm, setTenantForm] = useState({ email: '', password: '' });
  const [platformForm, setPlatformForm] = useState({ email: '', password: '' });
  const [quick, setQuick] = useState('');

  // One-click demo login: authenticate and enter the app in a single click.
  const oneClick = async (d, isPlatform) => {
    if (isPlatform) setPlatformForm({ email: d.email, password: d.password });
    else setTenantForm({ email: d.email, password: d.password });
    setQuick(d.email);
    try {
      const user = await login(d.email, d.password, isPlatform);
      toast.success(`Welcome, ${user.name || user.full_name || user.email || 'User'}!`);
      navigate(isPlatform ? '/admin' : '/select-outlet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally { setQuick(''); }
  };

  const DemoButtons = ({ accounts, isPlatform }) => (
    <div className="mt-4" data-testid="demo-accounts">
      <p className="text-xs font-medium text-foreground mb-2 text-center">One-click demo login</p>
      <div className="grid grid-cols-1 gap-2">
        {accounts.map((d) => (
          <button
            key={d.email}
            type="button"
            onClick={() => oneClick(d, isPlatform)}
            disabled={!!quick || loading}
            data-testid={`demo-login-${d.email.split('@')[0]}`}
            className="w-full flex items-center justify-between rounded-md border border-border px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-accent disabled:opacity-60"
          >
            <span>
              <span className="block text-xs font-medium text-foreground">{d.role}</span>
              <span className="block text-[10px] text-muted-foreground">{d.email}</span>
            </span>
            <span className="text-[10px] text-muted-foreground">
              {quick === d.email ? 'Signing in…' : 'Sign in →'}
            </span>
          </button>
        ))}
      </div>
      <p className="mt-2 text-[10px] text-muted-foreground text-center">
        One click signs you straight in as that role. No password to type.
      </p>
    </div>
  );

  const handleTenantLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(tenantForm.email, tenantForm.password, false);
      toast.success(`Welcome, ${user.name || user.full_name || user.email || 'User'}!`);
      navigate('/select-outlet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  const handlePlatformLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(platformForm.email, platformForm.password, true);
      toast.success(`Welcome, ${user.name || user.full_name || user.email || 'User'}!`);
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Brand ambient accents */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-secondary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <BentaLogo className="h-20 w-auto max-w-[280px]" />
          <p className="text-muted-foreground mt-3 text-sm">Business Enablement for No-Friction Transactions &amp; Accounting</p>
        </div>

        <Card className="border-border shadow-elevated">
          <CardHeader className="space-y-1">
            <CardTitle className="font-heading text-2xl text-foreground tracking-tight">Sign In</CardTitle>
            <CardDescription className="text-muted-foreground">
              Access your restaurant management workspace
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="tenant" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="tenant" data-testid="tenant-login-tab">
                  <Store className="w-4 h-4 mr-2" /> Restaurant
                </TabsTrigger>
                <TabsTrigger value="platform" data-testid="platform-login-tab">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Platform Admin
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tenant">
                <form onSubmit={handleTenantLogin} className="space-y-4 mt-5">
                  <div className="space-y-2">
                    <Label htmlFor="tenant-email">Email</Label>
                    <Input id="tenant-email" type="email" placeholder="owner@demo.com"
                      value={tenantForm.email}
                      onChange={(e) => setTenantForm({ ...tenantForm, email: e.target.value })}
                      data-testid="tenant-email-input" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tenant-password">Password</Label>
                    <Input id="tenant-password" type="password" placeholder="••••••••"
                      value={tenantForm.password}
                      onChange={(e) => setTenantForm({ ...tenantForm, password: e.target.value })}
                      data-testid="tenant-password-input" required />
                  </div>
                  <Button type="submit" className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold"
                    disabled={loading} data-testid="tenant-login-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Sign In
                  </Button>
                </form>
                <DemoButtons accounts={TENANT_DEMOS} isPlatform={false} />
              </TabsContent>

              <TabsContent value="platform">
                <form onSubmit={handlePlatformLogin} className="space-y-4 mt-5">
                  <div className="space-y-2">
                    <Label htmlFor="platform-email">Admin Email</Label>
                    <Input id="platform-email" type="email" placeholder="admin@benta.com"
                      value={platformForm.email}
                      onChange={(e) => setPlatformForm({ ...platformForm, email: e.target.value })}
                      data-testid="platform-email-input" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="platform-password">Password</Label>
                    <Input id="platform-password" type="password" placeholder="••••••••"
                      value={platformForm.password}
                      onChange={(e) => setPlatformForm({ ...platformForm, password: e.target.value })}
                      data-testid="platform-password-input" required />
                  </div>
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white font-bold"
                    disabled={loading} data-testid="platform-login-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Admin Sign In
                  </Button>
                </form>
                <DemoButtons accounts={PLATFORM_DEMOS} isPlatform={true} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-muted-foreground mt-6">© {new Date().getFullYear()} BENTA. All rights reserved.</p>
        <p className="text-center text-xs text-muted-foreground mt-1" data-testid="app-version">
          Version {process.env.REACT_APP_VERSION || "dev"}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
